// src/components/Admin/UserManager/PointLogs.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    CircularProgress,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Pagination,
    Stack,
    Typography,
    Chip,
    Divider,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import api from '../../../api/axios';

// ✅ 1. 설명 번역 맵
const DESCRIPTION_MAP = {
    'UNLOCK:widget.reactionBar': '미니홈 응원 위젯 해금',
    'UNLOCK:theme.background.emote': '미니홈 이모티콘 배경 해금',
    'UNLOCK:widget.titleShowcase': '미니홈 칭호 진열장 해금',
    'UNLOCK:widget.emoteShelf': '미니홈 이모티콘 진열장 해금',
    'UNLOCK:widget.textBox': '미니홈 텍스트 박스 해금',
};

// ✅ 2. 설명 변환 함수
const formatDescription = (description) => DESCRIPTION_MAP[description] || description || '-';

const TYPE_KO = { earn: '지급', use: '차감' };
const PAGE_SIZE = 15;

export default function PointLogs() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    /* 클라이언트 필터·정렬 상태 */
    const [search, setSearch] = useState('');
    const [typeFilt, setTypeFilt] = useState('all');
    const [sortBy, setSortBy] = useState('date_desc');
    const [page, setPage] = useState(1);

    /* 데이터 최초 로딩 */
    useEffect(() => {
        api
            .get('/api/management/point-logs/')
            .then((res) => {
                const data = res.data.results ?? res.data ?? [];
                setLogs(data.slice().sort((a, b) => new Date(b.date) - new Date(a.date)));
            })
            .catch((err) => {
                console.error('포인트(Leaf) 로그 조회 실패:', err);
                alert('포인트(Leaf) 로그를 불러오는 데 실패했습니다.');
            })
            .finally(() => setLoading(false));
    }, []);

    /* 필터·정렬 적용 */
    const processed = useMemo(() => {
        let arr = logs;

        if (search.trim()) {
            const q = search.toLowerCase();
            arr = arr.filter(
                (l) =>
                    (l.user || '').toLowerCase().includes(q) ||
                    formatDescription(l.description).toLowerCase().includes(q),
            );
        }

        if (typeFilt !== 'all') arr = arr.filter((l) => l.point_type === typeFilt);

        return [...arr].sort((a, b) => {
            if (sortBy === 'date_desc') return new Date(b.date) - new Date(a.date);
            if (sortBy === 'date_asc') return new Date(a.date) - new Date(b.date);
            if (sortBy === 'amount_desc') return b.amount - a.amount;
            if (sortBy === 'amount_asc') return a.amount - b.amount;
            return 0;
        });
    }, [logs, search, typeFilt, sortBy]);

    useEffect(() => setPage(1), [search, typeFilt, sortBy, logs]);

    const totalPages = Math.max(1, Math.ceil(processed.length / PAGE_SIZE));
    const paginated = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return processed.slice(start, start + PAGE_SIZE);
    }, [processed, page]);

    if (loading) {
        return <CircularProgress sx={{ display: 'block', mt: 4, mx: 'auto' }} />;
    }

    return (
        <Box sx={{ width: '100%', minWidth: 0 }}>
            {/* 검색·필터·정렬 UI */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    mb: 2,
                    flexWrap: 'wrap',
                    minWidth: 0,
                }}
            >
                <TextField
                    label="유저/설명"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: isMobile ? '100%' : 220 }}
                />

                <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 160 }}>
                    <InputLabel>구분</InputLabel>
                    <Select value={typeFilt} label="구분" onChange={(e) => setTypeFilt(e.target.value)}>
                        <MenuItem value="all">전체</MenuItem>
                        <MenuItem value="earn">지급</MenuItem>
                        <MenuItem value="use">차감</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 180 }}>
                    <InputLabel>정렬 기준</InputLabel>
                    <Select value={sortBy} label="정렬 기준" onChange={(e) => setSortBy(e.target.value)}>
                        <MenuItem value="date_desc">최신순</MenuItem>
                        <MenuItem value="date_asc">과거순</MenuItem>
                        <MenuItem value="amount_desc">금액 높은 순</MenuItem>
                        <MenuItem value="amount_asc">금액 낮은 순</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* ✅ 모바일: 카드 / ✅ PC: 테이블 */}
            {isMobile ? (
                <Stack spacing={1.25} sx={{ width: '100%', minWidth: 0 }}>
                    {paginated.length === 0 ? (
                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography sx={{ color: 'text.secondary' }}>🔍 조건에 맞는 로그가 없습니다.</Typography>
                        </Paper>
                    ) : (
                        paginated.map((log, idx) => {
                            const key = log.id ?? `${log.date}-${log.user}-${idx}`;
                            const typeLabel = TYPE_KO[log.point_type] || log.point_type || '-';
                            const amountText = `${log.amount > 0 ? '+' : ''}${Number(log.amount || 0).toLocaleString()} Leaf🌿`;
                            const dateText = dayjs(log.date).format('YYYY-MM-DD HH:mm');
                            const desc = formatDescription(log.description);

                            const chipColor =
                                log.point_type === 'earn' ? 'success' : log.point_type === 'use' ? 'error' : 'default';

                            return (
                                <Paper key={key} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                    {/* 상단: 유저 / 구분 */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                fontWeight: 800,
                                                flex: 1,
                                                minWidth: 0,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {log.user || '-'}
                                        </Typography>

                                        <Chip size="small" label={typeLabel} color={chipColor} variant="outlined" />
                                    </Box>

                                    {/* 금액 */}
                                    <Typography
                                        sx={{
                                            mt: 0.5,
                                            fontSize: '0.95rem',
                                            fontWeight: 800,
                                            color:
                                                log.point_type === 'earn'
                                                    ? 'success.main'
                                                    : log.point_type === 'use'
                                                        ? 'error.main'
                                                        : 'text.primary',
                                        }}
                                    >
                                        {amountText}
                                    </Typography>

                                    {/* 날짜 */}
                                    <Typography sx={{ mt: 0.25, fontSize: '0.8rem', color: 'text.secondary' }}>
                                        {dateText}
                                    </Typography>

                                    <Divider sx={{ my: 1 }} />

                                    {/* 설명 */}
                                    <Typography
                                        sx={{
                                            fontSize: '0.9rem',
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'anywhere',
                                        }}
                                    >
                                        {desc}
                                    </Typography>
                                </Paper>
                            );
                        })
                    )}

                    {/* 페이지네이션 */}
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, v) => setPage(v)}
                                showFirstButton
                                showLastButton
                                shape="rounded"
                                color="primary"
                                size="small"
                            />
                        </Box>
                    )}
                </Stack>
            ) : (
                <>
                    <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            '& tr:hover': { bgcolor: 'grey.100' },
                            '& td, & th': {
                                borderBottom: '1px solid #eee',
                                textAlign: 'center',
                                p: 1.25,
                                whiteSpace: 'nowrap',
                            },
                            '& th': { bgcolor: 'grey.50', fontWeight: 'bold' },
                        }}
                    >
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell width={160}>날짜</TableCell>
                                    <TableCell width={140}>유저</TableCell>
                                    <TableCell width={120}>구분</TableCell>
                                    <TableCell width={140}>금액</TableCell>
                                    <TableCell width={380}>설명</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {paginated.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} sx={{ py: 5 }}>
                                            🔍 조건에 맞는 로그가 없습니다.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginated.map((log, idx) => (
                                        <TableRow key={log.id ?? `${log.date}-${log.user}-${idx}`}>
                                            <TableCell>{dayjs(log.date).format('YYYY-MM-DD HH:mm')}</TableCell>
                                            <TableCell>{log.user || '-'}</TableCell>
                                            <TableCell>{TYPE_KO[log.point_type] || log.point_type}</TableCell>
                                            <TableCell>{`${log.amount > 0 ? '+' : ''}${Number(log.amount || 0).toLocaleString()} Leaf🌿`}</TableCell>
                                            <TableCell
                                                sx={{
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'anywhere',
                                                    textAlign: 'left',
                                                }}
                                            >
                                                {formatDescription(log.description)}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* 페이지네이션 */}
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, v) => setPage(v)}
                                showFirstButton
                                showLastButton
                                shape="rounded"
                                color="primary"
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}
